import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { AlertTriangle, Plus, Calendar, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { useRisks } from '@/hooks/queries';
import { RiskEditDialog } from '@/components/risks/RiskEditDialog';
import { RiskCreateDialog } from '@/components/risks/RiskCreateDialog';

const getRiskLevel = (score: number) => {
  if (score >= 15) return { label: 'High', color: 'bg-destructive text-destructive-foreground' };
  if (score >= 10) return { label: 'Medium', color: 'bg-warning text-warning-foreground' };
  if (score >= 5) return { label: 'Low', color: 'bg-compliant text-compliant-foreground' };
  return { label: 'Very Low', color: 'bg-muted text-muted-foreground' };
};

const statusColors = {
  identified: 'bg-warning text-warning-foreground',
  assessing: 'bg-accent text-accent-foreground',
  treating: 'bg-compliant text-compliant-foreground',
  monitoring: 'bg-primary text-primary-foreground',
  closed: 'bg-muted text-muted-foreground',
};

const statusLabels = {
  identified: 'Identified',
  assessing: 'Assessing',
  treating: 'Treating',
  monitoring: 'Monitoring',
  closed: 'Closed',
};

export default function RisksPage() {
  const { organization } = useAuth();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [riskLevelFilter, setRiskLevelFilter] = useState<string>('all');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState<any>(null);

  const { 
    risks, 
    isLoading, 
    createRisk, 
    updateRisk, 
    deleteRisk, 
    isCreating,
    isUpdating,
    isDeleting 
  } = useRisks(organization?.id, { status: statusFilter, riskLevel: riskLevelFilter });

  const handleCreate = async (data: any, links?: Array<{id: string; type: any; name: string}>) => {
    if (!organization?.id) {
      console.error('No organization ID');
      return;
    }
    
    const dataToSubmit = {
      ...data,
      org_id: organization.id,
    };
    
    createRisk(dataToSubmit, {
      onSuccess: async (newRisk: any) => {
        if (newRisk?.id && links && links.length > 0) {
          const { useLinkStore } = await import('@/stores/linkStore');
          const { bulkCreateLinks } = useLinkStore.getState();
          
          for (const link of links) {
            await bulkCreateLinks('risk', newRisk.id, link.type, [link.id]);
          }
        }
        setAddDialogOpen(false);
      },
      onError: (error) => {
        console.error('Error in handleCreate callback:', error);
      }
    });
  };

  const handleEdit = (risk: any) => {
    setSelectedRisk(risk);
    setEditDialogOpen(true);
  };

  const handleUpdate = (id: string, data: any) => {
    updateRisk({ id, data }, {
      onSuccess: () => {
        setEditDialogOpen(false);
        setSelectedRisk(null);
      },
    });
  };

  const handleDeleteClick = (risk: any) => {
    setSelectedRisk(risk);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedRisk) {
      deleteRisk(selectedRisk.id, {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          setSelectedRisk(null);
        },
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Risks</h1>
            <p className="text-muted-foreground">Monitor and manage organizational risks</p>
          </div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="h-3 bg-muted rounded w-full mb-2"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Risks</h1>
          <p className="text-muted-foreground">Monitor and manage organizational risks</p>
        </div>
        <Button className="gap-2" onClick={() => setAddDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          Add Risk
        </Button>
      </div>

      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="identified">Identified</SelectItem>
            <SelectItem value="assessed">Assessed</SelectItem>
            <SelectItem value="treated">Treated</SelectItem>
            <SelectItem value="monitored">Monitored</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>

        <Select value={riskLevelFilter} onValueChange={setRiskLevelFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by risk level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Risk Levels</SelectItem>
            <SelectItem value="high">High Risk</SelectItem>
            <SelectItem value="medium">Medium Risk</SelectItem>
            <SelectItem value="low">Low Risk</SelectItem>
            <SelectItem value="very low">Very Low Risk</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {risks && risks.length > 0 ? (
        <div className="space-y-4">
          {risks.map((risk) => {
            const riskLevel = getRiskLevel(risk.risk_score || 0);
            return (
              <Card key={risk.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-destructive" />
                        {risk.title}
                      </CardTitle>
                      <CardDescription>{risk.asset_or_process}</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={riskLevel.color}>
                        {riskLevel.label} ({risk.risk_score || 0})
                      </Badge>
                      <Badge className={statusColors[risk.status as keyof typeof statusColors]}>
                        {statusLabels[risk.status as keyof typeof statusLabels]}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Cause</p>
                      <p className="text-sm">{risk.cause || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Consequence</p>
                      <p className="text-sm">{risk.consequence || 'Not specified'}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Impact</p>
                        <p className="text-sm font-medium">{risk.impact || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Likelihood</p>
                        <p className="text-sm font-medium">{risk.likelihood || 'N/A'}</p>
                      </div>
                      {risk.residual_score && (
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Residual Score</p>
                          <p className="text-sm font-medium">{risk.residual_score}</p>
                        </div>
                      )}
                    </div>
                    {risk.next_review && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Next Review</p>
                          <p className="text-sm">{format(new Date(risk.next_review), 'MMM dd, yyyy')}</p>
                        </div>
                      </div>
                    )}
                    {risk.treatment && (
                      <div className="md:col-span-2">
                        <p className="text-sm text-muted-foreground mb-1">Treatment</p>
                        <p className="text-sm">{risk.treatment}</p>
                      </div>
                    )}
                    {risk.notes && (
                      <div className="md:col-span-2">
                        <p className="text-sm text-muted-foreground mb-1">Notes</p>
                        <p className="text-sm">{risk.notes}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end gap-2 pt-2 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(risk)}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteClick(risk)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No risks found</h3>
            <p className="text-muted-foreground text-center mb-4">
              Start identifying and assessing risks to improve your security posture.
            </p>
            <Button className="gap-2" onClick={() => setAddDialogOpen(true)}>
              <Plus className="h-4 w-4" />
              Add Your First Risk
            </Button>
          </CardContent>
        </Card>
      )}

      <RiskCreateDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSave={handleCreate}
        isCreating={isCreating}
      />

      {selectedRisk && (
        <RiskEditDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          risk={selectedRisk}
          onSave={handleUpdate}
          isSaving={isUpdating}
        />
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Risk</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedRisk?.title}"? This action cannot be undone.
              All related links will also be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}